// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, useLocation } from 'react-router-dom';
import LandingPage from './LandingPage';
import DebateResources from './DebateResources'; // Import DebateResources
import SessionPage from './SessionPage';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
  Container,
  Stack,
  Link as MuiLink,
  Paper
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email'; // Import the Email icon
import AdSenseUnit from './components/AdSenseUnit'; // Import AdSenseUnit

// Create a basic theme (optional, customize as needed)
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // Example blue
    },
    secondary: {
      main: '#ffa726', // Example orange
    },
    success: {
        main: '#2e7d32', // Example green
    },
    warning: {
        main: '#ed6c02', // Example amber/yellow
    },
    // Optional: Define a subtle background for the footer
    // background: {
    //   footer: '#f5f5f5' // A light grey example
    // }
  },
  typography: {
    button: {
      textTransform: 'none' // Prevent uppercase buttons if desired
    }
  }
});


const ContactPage = () => (
  <Container sx={{ mt: 4, mb: 4 }}>
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Contact Us
      </Typography>
      <Typography variant="body1" paragraph>
        Have questions, feedback, or need support regarding The Debate Timer? We'd love to hear from you!
      </Typography>
      <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
        Get in Touch
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <EmailIcon color="action" />
        <Typography variant="body1">
          You can reach us via email at:
        </Typography>
      </Box>
      <MuiLink
        href="mailto:info@thedebatetimer.com"
        variant="body1"
        sx={{ display: 'block', mt: 0.5, mb: 3, fontWeight: 'medium' }}
      >
        info@thedebatetimer.com
      </MuiLink>
      <Typography variant="body1" paragraph>
        We'll do our best to respond as quickly as possible.
      </Typography>
      <MuiLink component={RouterLink} to="/" sx={{ mt: 3, display: 'inline-block' }}>
        Go back home
      </MuiLink>
    </Paper>
  </Container>
);

const PrivacyPolicyPage = () => (
  <Container sx={{ mt: 4, mb: 4 }}>
    <Typography variant="h4" component="h1" gutterBottom>
      Privacy Policy for The Debate Timer
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      Last Updated: May 22, 2025 {/* <-- Manually update this date when policy changes */}
    </Typography>
    <Typography variant="body1" paragraph>
      This Privacy Policy describes how The Debate Timer (the "App", "we", "us", or "our") collects, uses, and handles your information when you use our web application accessible via thedebatetimer.com. {/* <-- Example: Replace with your actual URL or statement */}
    </Typography>
    <Typography variant="h6" component="h2" gutterBottom>
      Information We Collect
    </Typography>
    <Typography variant="body1" paragraph>
      We collect the following types of information:
    </Typography>
    <ul>
      <li><Typography variant="body1">**Generated User Identifier:** When you first use the App, we generate a random, anonymous identifier (`debateUserId`) which is stored in your browser's `localStorage`. This helps the App recognize you if you rejoin a session from the same browser, but it is not linked to any personally identifiable information unless you provide such information elsewhere.</Typography></li>
      <li><Typography variant="body1">**Session Data:** When you participate in a debate session, we store information related to that specific session in our Firebase Realtime Database. This includes:</Typography>
        <ul>
          <li><Typography variant="body2">The debate format selected (LD or PF).</Typography></li>
          <li><Typography variant="body2">Which roles (e.g., Affirmative, Judge, First Pro) are assigned to which generated User Identifiers.</Typography></li>
          <li><Typography variant="body2">The current state of the debate (current segment, timer end times, remaining prep time for each side).</Typography></li>
          <li><Typography variant="body2">Participant status (online/offline presence indicators linked to the generated User Identifier).</Typography></li>
          <li><Typography variant="body2">Notes entered by the user assigned the 'Judge' role during the session.</Typography></li>
        </ul>
      </li>
      <li><Typography variant="body1">**Usage Data (via Firebase Analytics):** We use Firebase Analytics to collect standard web analytics information about how users interact with the App. This may include information like pages visited, features used, browser type, device type, general geographic location (e.g., country), and session duration. This data is aggregated and helps us understand usage patterns and improve the App.</Typography></li>
      <li><Typography variant="body1">**Cookies (via Google AdSense and Firebase):** We use Google AdSense to display advertisements, which uses cookies to serve ads based on a user's prior visits to this website or other websites. Google's use of advertising cookies enables it and its partners to serve ads to your users based on their visit to your sites and/or other sites on the Internet. Firebase services may also use cookies for functionality and analytics. You can opt out of personalized advertising by visiting Google's Ads Settings.</Typography></li>
    </ul>
    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      How We Use Information
    </Typography>
    <ul>
      <li><Typography variant="body1">**To Operate the App:** Session Data and the Generated User Identifier are essential for the core functionality of the App, such as synchronizing timers, managing roles, tracking participant presence, and displaying judge notes in real-time.</Typography></li>
      <li><Typography variant="body1">**To Improve the App:** Usage Data helps us understand how the App is being used, identify areas for improvement, and fix bugs.</Typography></li>
      <li><Typography variant="body1">**To Display Advertisements:** We use Google AdSense to support the App. Cookies are used by Google to provide relevant advertising.</Typography></li>
    </ul>
    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      Data Sharing and Third Parties
    </Typography>
    <Typography variant="body1" paragraph>
      We use third-party services to provide and improve the App:
    </Typography>
    <ul>
      <li><Typography variant="body1">**Google Firebase:** We use Firebase Realtime Database for storing and synchronizing session data, and Firebase Analytics for usage statistics. Data stored in Firebase is subject to Google's Privacy Policy: <MuiLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</MuiLink></Typography></li>
      <li><Typography variant="body1">**Google AdSense:** We use AdSense to display ads. AdSense collects data as described in Google's Privacy Policy and Advertising Policies. You can manage your ad preferences via Google's Ad Settings: <MuiLink href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">https://adssettings.google.com/</MuiLink></Typography></li>
    </ul>
    <Typography variant="body1" paragraph>
      We do not sell your personal information. We only share data with these third parties as necessary to provide the App's functionality, conduct analytics, or display advertising.
    </Typography>
    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      Data Retention and Deletion
    </Typography>
    <ul>
      <li><Typography variant="body1">**Generated User Identifier:** Stored in your browser's `localStorage` and persists until you clear your browser data.</Typography></li>
      <li><Typography variant="body1">**Session Data:** Session data stored in Firebase is intended to be temporary, existing only for the duration of an active debate round. Specifically, notes entered by the Judge are configured in the App to be automatically deleted from our database when the Judge disconnects from the session (e.g., closes the tab/browser). Other session data (like roles, timer states) may persist for a short period after all users leave but is not intended for long-term storage and may be periodically cleared.</Typography></li>
      <li><Typography variant="body1">**Analytics Data:** Aggregated usage data may be retained by Firebase Analytics according to Google's data retention policies.</Typography></li>
    </ul>
     <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      Cookies
    </Typography>
     <Typography variant="body1" paragraph>
       As mentioned, cookies are used by Firebase for functionality/analytics and by Google AdSense for advertising. You can control or delete cookies through your web browser settings. Please note that disabling cookies might affect the functionality of the App or the relevance of ads shown.
     </Typography>
    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      Security
    </Typography>
    <Typography variant="body1" paragraph>
      We take reasonable measures to protect the information handled by our App. However, no internet transmission or electronic storage is 100% secure. We rely on Firebase's security infrastructure for data stored in the database.
    </Typography>
    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      Children's Privacy
    </Typography>
    <Typography variant="body1" paragraph>
      This App is not intended for use by children under the age of 13 (or the relevant age in your jurisdiction). We do not knowingly collect personal information from children.
    </Typography>
    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
      Changes to This Privacy Policy
    </Typography>
    <Typography variant="body1" paragraph>
      We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
    </Typography>
    <MuiLink component={RouterLink} to="/" sx={{ mt: 2, display: 'inline-block' }}>
      Go back home
    </MuiLink>
  </Container>
);

const AboutPage = () => (
  <Container sx={{ mt: 4, mb: 4 }}>
    <Typography variant="h4" component="h1" gutterBottom>
      About The Debate Timer
    </Typography>
    <Typography variant="body1" paragraph>
      Welcome to The Debate Timer (or Debate Session Manager)! This tool was built to streamline the management of online competitive debate rounds, specifically for **Lincoln-Douglas (LD)** and **Public Forum (PF)** formats.
    </Typography>
    <Typography variant="body1" paragraph>
      Keeping track of speech times, prep time, participant roles, and judge notes across different platforms during an online round can be challenging. This application aims to solve that by providing a single, synchronized interface for all participants.
    </Typography>
    <Typography variant="h6" component="h2" gutterBottom>
      Key Features:
    </Typography>
    <ul>
      <li><Typography variant="body1">**Format Selection:** Easily start or join sessions tailored for LD or PF debate structures.</Typography></li>
      <li><Typography variant="body1">**Role Management:** Clearly defined roles (Affirmative, Negative, Pro Speakers, Con Speakers, Judge) ensure everyone knows their place.</Typography></li>
      <li><Typography variant="body1">**Synchronized Timers:** Real-time, shared timers for both speech segments and preparation time keep everyone on the same clock. Prep time is tracked per side.</Typography></li>
      <li><Typography variant="body1">**Live Participant Status:** See which roles are filled and whether participants are currently online within the session.</Typography></li>
      <li><Typography variant="body1">**Integrated Judge Notes:** Judges have a dedicated space to type notes for each segment, synchronized in real-time (and downloadable!).</Typography></li>
      <li><Typography variant="body1">**Real-Time Updates:** Built using Firebase, all session data (timers, roles, status, notes) updates instantly for all connected users.</Typography></li>
      <li><Typography variant="body1">**Simple Access:** No complex sign-ups required. Just start a session to get a shareable ID, or join using an existing ID.</Typography></li>
    </ul>
    <Typography variant="body1" paragraph sx={{ mt: 2 }}>
      Whether you're a competitor needing accurate timing, a judge managing the round flow, or an observer following along, this tool is designed to make the online debate experience smoother and more focused.
    </Typography>
    <Typography variant="body1" paragraph>
      This project is actively developed [Optional: Mention if it's a personal project, open source, etc. E.g., "as a personal project to help the debate community."]. Feedback is always welcome! [Optional: Add contact info or link if you have one].
    </Typography>
    <MuiLink component={RouterLink} to="/" sx={{ mt: 2, display: 'inline-block' }}>
      Go back home
    </MuiLink>
  </Container>
);

// --- Main App Content Component (to use hooks) ---
function AppContent() {
  const location = useLocation(); // Get current location
  const isSessionPage = location.pathname.startsWith('/session/'); // Check if it's a session page

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Normalize CSS and apply baseline styles */}
      {/* Use a Box to wrap content and footer for sticky footer */}
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Main content area */}
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/session/:sessionId" element={<SessionPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/resources" element={<DebateResources />} /> {/* Add route for resources */}
          </Routes>
        </Box>

        {/* --- Conditionally Render Ad Above Footer --- */}
        {!isSessionPage && ( // Only show if NOT on a session page
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center', mt: 2 }}> {/* Added padding/margin for spacing */}
            <AdSenseUnit />
          </Box>
        )}
        {/* --- End Ad --- */}

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 2, // Vertical padding
            px: 2, // Horizontal padding
            mt: 'auto', // Push footer to bottom
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[200]
                : theme.palette.grey[800], // Subtle background color
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', sm: 'row' }} // Stack vertically on small screens, row on larger
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" color="text.secondary">
                {'© '}
                {new Date().getFullYear()}
                {' The Debate Timer'} {/* Replace with your app name if different */}
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <MuiLink component={RouterLink} to="/contact" variant="body2" color="text.secondary">
                  Contact
                </MuiLink>
                <MuiLink component={RouterLink} to="/about" variant="body2" color="text.secondary">
                  About
                </MuiLink>
                <MuiLink component={RouterLink} to="/privacy" variant="body2" color="text.secondary">
                  Privacy Policy
                </MuiLink>
                <MuiLink component={RouterLink} to="/resources" variant="body2" color="text.secondary">
                  Resources
                </MuiLink>
                {/* Add Terms of Service link here later if needed */}
              </Stack>
            </Stack>
          </Container>
        </Box>
        {/* --- End Footer --- */}
      </Box>
    </ThemeProvider>
  );
}

// --- Main App Component (provides Router context) ---
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
